import React, { useState, useEffect } from 'react';
import { documentService } from '../services/api';
import QAKnowledgeBaseSection from '../components/QAKnowledgeBaseSection';
import DocumentsSection from '../components/DocumentsSection';
import HolidaySection from '../components/HolidaySection';
import { useLanguage } from '../contexts/LanguageContext';

const Policy = () => {
  const { t } = useLanguage();

  // Initialize activeTab from localStorage or default to 'qa'
  const [activeTab, setActiveTab] = useState(() => {
    const savedTab = localStorage.getItem('policy-active-tab');
    return savedTab && ['qa', 'documents', 'holiday'].includes(savedTab) ? savedTab : 'qa';
  });

  // Document section states
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Save active tab to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('policy-active-tab', activeTab);
  }, [activeTab]);

  // Fetch documents when Documents tab is selected
  useEffect(() => {
    const fetchDocuments = async () => {
      if (activeTab === 'documents') {
        try {
          setDocumentsLoading(true);
          setDocumentsError(null);
          const response = await documentService.getDocuments();

          // Handle new command pattern response structure
          if (response.success && response.data) {
            setDocuments(response.data.items || []);
          } else {
            // Handle legacy response format for backward compatibility
            setDocuments(response.items || []);
          }
        } catch (err) {
          console.error('Failed to fetch documents:', err);
          setDocumentsError('Failed to load documents. Please try again later.');
        } finally {
          setDocumentsLoading(false);
        }
      }
    };

    fetchDocuments();
  }, [activeTab]);

  // Document handlers
  const handleAddDocument = async (documentData) => {
    try {
      setIsSaving(true);
      const response = await documentService.addDocument(documentData);

      // Handle new command pattern response structure
      if (response.success && response.data) {
        setDocuments([...documents, response.data]);
        setDocumentsError(null);
      } else if (response.document) {
        // Handle legacy response format for backward compatibility
        setDocuments([...documents, response.document]);
        setDocumentsError(null);
      } else {
        setDocumentsError(response.message || 'Failed to add document. Please try again.');
      }
    } catch (err) {
      console.error('Failed to add document:', err);
      setDocumentsError('Failed to add document. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleUpdateDocument = async (documentData) => {
    try {
      setIsSaving(true);
      // Remove id and clid from the payload to avoid duplicate parameter issues
      const { id, clid, ...updatePayload } = documentData;
      const response = await documentService.updateDocument(id, updatePayload);

      // Handle new command pattern response structure
      if (response.success && response.data) {
        setDocuments(documents.map(doc =>
          doc.id === id ? response.data : doc
        ));
        setDocumentsError(null);
      } else if (response.document) {
        // Handle legacy response format for backward compatibility
        setDocuments(documents.map(doc =>
          doc.id === id ? response.document : doc
        ));
        setDocumentsError(null);
      } else {
        setDocumentsError(response.message || 'Failed to update document. Please try again.');
      }
    } catch (err) {
      console.error('Failed to update document:', err);
      setDocumentsError('Failed to update document. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Are you sure you want to delete this document?')) {
      return;
    }

    try {
      setIsSaving(true);
      const response = await documentService.deleteDocument(documentId);

      // Handle new command pattern response structure
      if (response.success) {
        setDocuments(documents.filter(doc => doc.id !== documentId));
        setDocumentsError(null);
      } else {
        setDocumentsError(response.message || 'Failed to delete document. Please try again.');
      }
    } catch (err) {
      console.error('Failed to delete document:', err);
      setDocumentsError('Failed to delete document. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="container mx-auto px-4 py-6">
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Knowledge Base</h1>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          className={`py-2 px-4 font-medium text-sm focus:outline-none ${
            activeTab === 'qa'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('qa')}
        >
          QA Knowledge Base
        </button>
        <button
          className={`py-2 px-4 font-medium text-sm focus:outline-none ${
            activeTab === 'documents'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('documents')}
        >
          Documents
        </button>
        <button
          className={`py-2 px-4 font-medium text-sm focus:outline-none ${
            activeTab === 'holiday'
              ? 'text-blue-600 border-b-2 border-blue-600'
              : 'text-gray-500 hover:text-gray-700'
          }`}
          onClick={() => setActiveTab('holiday')}
        >
          {t('nav.blockSchedule')}
        </button>
      </div>

      {/* QA Knowledge Base Tab */}
      {activeTab === 'qa' && (
        <QAKnowledgeBaseSection />
      )}

      {/* Documents Tab */}
      {activeTab === 'documents' && (
        <div>
          {documentsError && (
            <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-6" role="alert">
              <p>{documentsError}</p>
            </div>
          )}

          {documentsLoading ? (
            <div className="flex justify-center items-center h-32">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#075e54]"></div>
            </div>
          ) : (
            <DocumentsSection
              documents={documents}
              onAddDocument={handleAddDocument}
              onUpdateDocument={handleUpdateDocument}
              onDeleteDocument={handleDeleteDocument}
              isSaving={isSaving}
            />
          )}
        </div>
      )}

      {/* Holiday Tab */}
      {activeTab === 'holiday' && (
        <HolidaySection />
      )}
    </div>
  );
};

export default Policy;
